/*eslint max-lines-per-function: ["error", 180]*/
import React, { useMemo, useState } from 'react';
import { Button, Card, Tab } from 'react-bootstrap';

import { useAuth } from '../../../common/hooks/useAuth';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { Role } from '../../../common/spdCore/authorization/roles';
import {
  CreateUpdateUserDto,
  GetUserDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import {
  userData,
  validate,
} from '../../../common/spdCore/contexts/UserContext';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import { UserEditor } from '../../components/Users/UserEditor';
import { Users } from '../../components/Users/Users';
import {
  GridCountFunction,
  GridGetterFunction,
} from '../../../common/components/Grid/GridDataTypes';
import { TabsHistory } from '../../../app/components/TabsHistory';

export const UsersView: React.FC = () => {
  const spd = useSpdCore();
  const auth = useAuth();
  const isSuperAdmin = auth.check([Role.SuperAdmin]);

  const [show, setShow] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [user, setUser] = useState<GetUserDto>();

  const [refresh, setRefresh] = useState<boolean>(false);

  const handleAdd = () => {
    setShow(true);
    setUser(undefined);
  };

  const addEditUser = async (userCU: CreateUpdateUserDto, id?: string) => {
    const status = await validate(userCU);
    if (status.valid) {
      try {
        if (id) {
          await spd.users.update(id, userCU);
        } else {
          await spd.users.create(userCU);
        }
        setShow(false);
        setRefresh(!refresh);
        return {
          saved: true,
          errors: [],
        };
      } catch (response) {
        return {
          saved: false,
          errors: await responseErrors(response as ResponseError),
        };
      }
    } else {
      return {
        saved: false,
        errors: status.errors,
      };
    }
  };

  const handleClose = () => {
    setShow(false);
    setRefresh(!refresh);
  };

  const handleSyncClaims = async () => {
    setSyncing(true);
    try {
      await spd.users.syncFirebaseClaims();
      alert('Synchronizacja Firebase claims zakończona.');
    } catch {
      alert('Błąd synchronizacji claims.');
    } finally {
      setSyncing(false);
    }
  };

  const handleClick = async (id: string) => {
    const getUser = await spd.users.getById(id);
    setUser(getUser);
    setShow(true);
  };

  const getData = useMemo(
    () => (mode?: string) =>
      ((
        pageSize,
        pageNumber,
        orderBy,
        orderDirection,
        filterText,
        filterColumns,
      ) =>
        spd.users.getAllByPortion(
          pageSize,
          pageNumber,
          orderBy,
          orderDirection,
          filterText,
          mode,
          filterColumns,
        )) as GridGetterFunction,
    [spd.users],
  );

  const getCount = useMemo(
    () => (mode?: string) =>
      ((filterText, filterColumns) =>
        spd.users.getAllCount(
          filterText,
          mode,
          filterColumns,
        )) as GridCountFunction,
    [spd.users],
  );

  return (
    <Card className="main-card">
      <Card.Body className="m-3">
        <Card.Title className="pb-3">Użytkownicy</Card.Title>
        <TabsHistory activeKey="all" id="users" className="mb-3">
          <Tab eventKey="all" title="Wszyscy" className="m-3">
            <Users
              getData={getData()}
              getCount={getCount()}
              onRowClick={handleClick}
              refresh={refresh}
              withFilterInColumns
            />
          </Tab>
          <Tab eventKey="workers" title="Pracownicy" className="m-3">
            <Users
              getData={getData('workers')}
              getCount={getCount('workers')}
              onRowClick={handleClick}
              refresh={refresh}
              withFilterInColumns
            />
          </Tab>
          <Tab eventKey="examined" title="Badani" className="m-3">
            <Users
              getData={getData('examined')}
              getCount={getCount('examined')}
              onRowClick={handleClick}
              refresh={refresh}
              withFilterInColumns
            />
          </Tab>
          <Tab eventKey="external" title="Zewnętrzni" className="m-3">
            <Users
              getData={getData('external')}
              getCount={getCount('external')}
              onRowClick={handleClick}
              refresh={refresh}
              withFilterInColumns
            />
          </Tab>
        </TabsHistory>
        <div className="m-3 mt-0 d-flex gap-2">
          <Button variant="outline-primary" onClick={handleAdd}>
            Nowy użytkownik
          </Button>
          {isSuperAdmin && (
            <Button
              variant="outline-secondary"
              onClick={handleSyncClaims}
              disabled={syncing}
            >
              {syncing ? 'Synchronizacja...' : 'Synchronizuj Firebase claims'}
            </Button>
          )}
        </div>
        <UserEditor
          id={user?.id}
          user={userData(user)}
          hasCertificate={user?.hasCertificate}
          healthcareFacilities={user?.usersInHealthcareFacility
            ?.filter(o => o !== null && o !== undefined)
            ?.map(hf => hf.healthcareFacility)}
          onCompleted={addEditUser}
          show={show}
          handleClose={handleClose}
        />
      </Card.Body>
    </Card>
  );
};
